/**
 * Getting Started - Languages Component
 * Alpine.js component for the two-step language selection flow
 */

import { getCountryCode as getCountryCodeFromMap } from '../lang-country-map.js';

/**
 * Languages selection component factory
 * Handles native language selection and other languages with learning flags
 */
export function gettingStartedLanguages(config = {}) {
    // Popular languages to show at top (codes)
    const POPULAR_LANGS = ['EN', 'ES', 'FR', 'DE', 'PT', 'IT', 'ZH', 'JA', 'KO', 'RU', 'AR', 'HI'];

    // Detect browser locale language code (e.g., "en-US" -> "EN")
    const detectBrowserLang = () => {
        const locale = navigator.language || navigator.userLanguage || '';
        return locale.split('-')[0].toUpperCase();
    };

    const browserLang = detectBrowserLang();

    // Limits
    const MAX_NATIVE_LANGS = 3;
    const MAX_TOTAL_LANGS = 10;

    return {
        // State
        step: 1,
        nativeLangs: config.nativeLangs || [],  // Array of {code, name}
        otherLangs: config.otherLangs || [],
        allLangs: config.allLangs || {},
        levelLabels: config.levelLabels || null,  // Translated level labels from Twig
        searchQuery: '',
        showAllLangs: false,
        pickerSearchQuery: '',

        // Step 2 modes: 'list' (viewing languages), 'add' (picker open), 'edit' (editing a language)
        step2Mode: 'list',
        editingIndex: -1,

        // Constants exposed to template
        maxNativeLangs: MAX_NATIVE_LANGS,
        maxTotalLangs: MAX_TOTAL_LANGS,

        get totalLangsCount() {
            return this.nativeLangs.length + this.otherLangs.length;
        },

        get canAddMoreNativeLangs() {
            return this.nativeLangs.length < MAX_NATIVE_LANGS && this.totalLangsCount < MAX_TOTAL_LANGS;
        },

        get canAddMoreLangs() {
            return this.totalLangsCount < MAX_TOTAL_LANGS;
        },

        // Computed
        get canProceedToStep2() {
            return this.nativeLangs.length > 0;
        },

        get sortedLangs() {
            // Sort languages alphabetically for dropdown
            return Object.entries(this.allLangs)
                .sort(([, a], [, b]) => a.localeCompare(b));
        },

        get popularLangs() {
            // Put browser locale language first if it exists
            let langs = [...POPULAR_LANGS];
            if (browserLang && this.allLangs[browserLang] && langs.includes(browserLang)) {
                langs = [browserLang, ...langs.filter(code => code !== browserLang)];
            }
            return langs
                .filter(code => this.allLangs[code])
                .map(code => ({ code, name: this.allLangs[code], countryCode: getCountryCodeFromMap(code) }));
        },

        getCountryCode(code) {
            return getCountryCodeFromMap(code);
        },

        get filteredLangs() {
            const query = this.searchQuery.toLowerCase().trim();
            let langs = Object.entries(this.allLangs)
                .map(([code, name]) => ({ code, name, countryCode: getCountryCodeFromMap(code) }))
                .sort((a, b) => a.name.localeCompare(b.name));

            if (query) {
                // Filter by search query
                langs = langs.filter(l =>
                    l.name.toLowerCase().includes(query) ||
                    l.code.toLowerCase().includes(query)
                );
            } else if (!this.showAllLangs) {
                // Show only popular langs when not searching and not expanded
                langs = this.popularLangs;
            }

            return langs;
        },

        get otherLangsCount() {
            return Object.keys(this.allLangs).length - POPULAR_LANGS.length;
        },

        get practiceLangs() {
            return this.otherLangs.filter(l => l.learning === true && l.code);
        },

        // Popular languages for picker (excludes native langs)
        get pickerPopularLangs() {
            const nativeCodes = new Set(this.nativeLangs.map(l => l.code));
            return POPULAR_LANGS
                .filter(code => !nativeCodes.has(code) && this.allLangs[code])
                .map(code => ({ code, name: this.allLangs[code], countryCode: getCountryCodeFromMap(code) }));
        },

        // All languages for picker (excludes native langs), sorted alphabetically
        get pickerAllLangs() {
            const nativeCodes = new Set(this.nativeLangs.map(l => l.code));
            return Object.entries(this.allLangs)
                .filter(([code]) => !nativeCodes.has(code))
                .map(([code, name]) => ({ code, name, countryCode: getCountryCodeFromMap(code) }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        // Filtered languages for search in picker
        get pickerFilteredLangs() {
            const query = this.pickerSearchQuery.toLowerCase().trim();
            if (!query) return [];

            return this.pickerAllLangs.filter(l =>
                l.name.toLowerCase().includes(query) ||
                l.code.toLowerCase().includes(query)
            );
        },

        // Methods
        init() {
            // No initialization needed - Alpine handles reactivity
        },

        selectNativeLang(code, name) {
            const index = this.nativeLangs.findIndex(l => l.code === code);
            if (index >= 0) {
                // Toggle off if already selected
                this.nativeLangs.splice(index, 1);
            } else if (this.canAddMoreNativeLangs) {
                // Add if under limit
                this.nativeLangs.push({ code, name });
            }
            this.searchQuery = '';
        },

        isNativeLang(code) {
            return this.nativeLangs.some(l => l.code === code);
        },

        toggleShowAll() {
            this.showAllLangs = !this.showAllLangs;
        },

        goToStep2() {
            if (!this.canProceedToStep2) {
                document.getElementById('nativeLangInput')?.focus();
                return;
            }
            this.step = 2;
            this.$nextTick(() => {
                if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                }
            });
        },

        goToStep1() {
            this.step = 1;
        },

        // Open the language picker to add a new language
        openPicker() {
            this.step2Mode = 'add';
            this.editingIndex = -1;
            this.pickerSearchQuery = '';
            this.$nextTick(() => {
                this.$refs.pickerSearch?.focus();
            });
        },

        // Close picker and return to list view
        closePicker() {
            this.step2Mode = 'list';
            this.pickerSearchQuery = '';
        },

        // Add a language from the picker and open it for editing
        addLanguageFromPicker(code) {
            if (this.isLanguageAdded(code)) return;

            const newIndex = this.otherLangs.length;
            this.otherLangs.push({
                code: code,
                level: null,  // No default level - user must select
                learning: false  // Will be auto-set when level is selected
            });

            // Switch to edit mode for the new language
            this.step2Mode = 'edit';
            this.editingIndex = newIndex;
            this.pickerSearchQuery = '';

            this.$nextTick(() => {
                if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                }
            });
        },

        // Set proficiency level and auto-toggle practice checkbox
        setLevel(index, level) {
            this.otherLangs[index].level = level;
            // Auto-select practice for beginner (0) and intermediate (1)
            if (level === 0 || level === 1) {
                this.otherLangs[index].learning = true;
            } else {
                // For advanced/fluent, don't auto-select (but don't uncheck if already checked)
            }
        },

        // Open a language for editing
        editLanguage(index) {
            this.step2Mode = 'edit';
            this.editingIndex = index;
        },

        // Done editing, return to list view
        doneEditing() {
            this.step2Mode = 'list';
            this.editingIndex = -1;
        },

        getLevelLabel(level) {
            if (level === null || level === undefined) return this.levelLabels?.notSet || 'Not set';
            const labels = this.levelLabels || {
                0: 'Beginner',
                1: 'Intermediate',
                2: 'Advanced',
                3: 'Proficient'
            };
            return labels[level] || labels[0] || 'Beginner';
        },

        isLanguageAdded(code) {
            return this.nativeLangs.some(l => l.code === code) || this.otherLangs.some(l => l.code === code);
        },

        removeLanguage(index) {
            this.otherLangs.splice(index, 1);
            this.step2Mode = 'list';
            this.editingIndex = -1;
        },

        handleSubmit() {
            if (this.nativeLangs.length === 0) {
                alert(window.t('common.getting_started.select_native_language'));
                this.step = 1;
                return false;
            }
            return true;
        }
    };
}
